import { Button, Card, Form, Input, Space, message } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type ApiError = { response?: { data?: { message?: string } } };
type Application = { id: string; record_version: number };

function asList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  return [];
}

export default function AdmissionDecisionPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [submitForm] = Form.useForm();
  const [waitlistForm] = Form.useForm();

  const application = useQuery({
    queryKey: ['education-admission-version', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const rows = asList<Application>((await api.get('/education/admissions')).data);
      return rows.find((row) => row.id === id) || null;
    },
  });

  useEffect(() => {
    const version = application.data?.record_version;
    if (version == null) return;
    submitForm.setFieldValue('record_version', version);
    waitlistForm.setFieldValue('record_version', version);
  }, [application.data?.record_version, submitForm, waitlistForm]);

  const submit = useMutation({
    mutationFn: (values: { record_version: number }) =>
      api.post(`/education/admissions/${id}/decision`, {
        record_version: values.record_version ?? application.data?.record_version,
      }),
    onSuccess: () => message.success(t('saved')),
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const waitlist = useMutation({
    mutationFn: (values: { record_version: number }) =>
      api.post(`/education/admissions/${id}/transition`, {
        status: 'waitlisted',
        record_version: values.record_version ?? application.data?.record_version,
      }),
    onSuccess: () => message.success(t('saved')),
    onError: (error: ApiError) => message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('decision')}
      description={t('admissionsIntro')}
      extra={
        <Button onClick={() => navigate(`/education/admissions?${params.toString()}`)}>
          {t('backToAdmissions')}
        </Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" title={t('submitDecision')} loading={application.isLoading}>
          <Form form={submitForm} layout="vertical" onFinish={(values) => submit.mutate(values)} style={{ maxWidth: 480 }}>
            <Form.Item name="record_version" hidden><Input /></Form.Item>
            <Button type="primary" htmlType="submit" loading={submit.isPending}>{t('submitDecision')}</Button>
          </Form>
        </Card>
        <Card size="small" title={t('waitlisted', { defaultValue: 'Waitlist' })}>
          <Form form={waitlistForm} layout="vertical" onFinish={(values) => waitlist.mutate(values)} style={{ maxWidth: 480 }}>
            <Form.Item name="record_version" hidden><Input /></Form.Item>
            <Button htmlType="submit" loading={waitlist.isPending}>
              {t('waitlisted', { defaultValue: 'waitlisted' })}
            </Button>
          </Form>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
